'use client';

import { format } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import React, { useState } from 'react';

import { Button } from '../../primitives/button';
import { Calendar } from '../../primitives/calendar';
import { Label } from '../../primitives/label';
import { Popover, PopoverContent, PopoverTrigger } from '../../primitives/popover';
import { useLocalization } from '../../theme';
import { cn } from '../../utils/cn';

export interface UzDateRange {
  from: Date | null;
  to: Date | null;
}

export interface UzDateRangePreset {
  /** Stable key used as the option value (e.g. `'last_7_days'`). */
  value: string;
  label: string;
  /** Computes the range when the operator picks this preset. */
  range: () => UzDateRange;
}

export interface UzDateRangePickerProps {
  label?: string;
  hint?: string;
  error?: string;
  placeholder?: string;
  value?: UzDateRange | null;
  defaultValue?: UzDateRange | null;
  onValueChange?: (value: UzDateRange) => void;
  disabled?: boolean;
  /** Display format for the trigger label (per-day). Defaults to AppConfig's dateFormat. */
  displayFormat?: string;
  /** Optional preset list shown in a column to the left of the calendar. */
  presets?: ReadonlyArray<UzDateRangePreset>;
  /** Value of the preset that's currently "active" - driven by the parent. */
  activePresetValue?: string | null;
  /** Called when the operator clicks a preset (the parent typically also calls
   *  `onValueChange` with the same range). */
  onPresetSelect?: (preset: UzDateRangePreset) => void;
  containerClassName?: string;
  triggerClassName?: string;
}

const isSameDay = (a: Date | null | undefined, b: Date | null | undefined): boolean => {
  if (!a || !b) return false;
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
};

/**
 * Date-range picker - single trigger, single popover. Renders an optional
 * preset column (e.g. "Today", "Last 7 days") next to a range-mode calendar.
 * Emits `{ from, to }` as soon as both ends are set.
 *
 * Both ends are `Date | null` so the parent can distinguish "unset" from
 * "epoch 0". Pass `value={null}` (or omit) to render a placeholder trigger.
 */
export const UzDateRangePicker = ({
  label,
  hint,
  error,
  placeholder = 'Pick a date range',
  value,
  defaultValue,
  onValueChange,
  disabled,
  displayFormat,
  presets,
  activePresetValue,
  onPresetSelect,
  containerClassName,
  triggerClassName,
}: UzDateRangePickerProps): React.JSX.Element => {
  const localization = useLocalization();
  const dayFmt = displayFormat ?? localization.dateFormat;

  const [open, setOpen] = useState(false);
  const [internal, setInternal] = useState<UzDateRange>(defaultValue ?? { from: null, to: null });
  const isControlled = value !== undefined;
  const selected = isControlled ? (value ?? { from: null, to: null }) : internal;

  const commit = (next: UzDateRange): void => {
    if (!isControlled) setInternal(next);
    onValueChange?.(next);
  };

  const handleCalendar = (
    range: { from?: Date | undefined; to?: Date | undefined } | undefined,
  ): void => {
    const next: UzDateRange = {
      from: range?.from ?? null,
      to: range?.to ?? null,
    };
    commit(next);
    // Intentionally NOT closing on range-complete. Operators frequently
    // tweak one end after picking both ("the end date is actually a day
    // later"); auto-closing forces a re-open + re-pick. They close
    // explicitly via Apply or by clicking outside.
  };

  // What to show on the trigger. Three cases - preset active, full range,
  // partial range, or empty.
  const triggerLabel = (() => {
    if (activePresetValue && presets) {
      const p = presets.find((pp) => pp.value === activePresetValue);
      if (p) return p.label;
    }
    if (selected.from && selected.to) {
      if (isSameDay(selected.from, selected.to)) return format(selected.from, dayFmt);
      return `${format(selected.from, dayFmt)} - ${format(selected.to, dayFmt)}`;
    }
    if (selected.from) return `${format(selected.from, dayFmt)} - …`;
    return placeholder;
  })();

  return (
    <div className={cn('space-y-1.5', containerClassName)}>
      {label && <Label className={cn(disabled && 'opacity-60')}>{label}</Label>}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            disabled={disabled}
            className={cn(
              'min-w-[180px] justify-start text-left font-normal',
              !selected.from && !activePresetValue && 'text-muted-foreground',
              error && 'border-destructive focus-visible:ring-destructive',
              triggerClassName,
            )}
            aria-invalid={!!error || undefined}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {triggerLabel}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="end">
          <div className="flex flex-col sm:flex-row">
            {presets && presets.length > 0 && (
              <div className="flex max-h-[320px] flex-col gap-0.5 overflow-y-auto border-b p-2 sm:border-r sm:border-b-0">
                {presets.map((p) => {
                  const active = p.value === activePresetValue;
                  return (
                    <button
                      key={p.value}
                      type="button"
                      onClick={() => {
                        const r = p.range();
                        commit(r);
                        onPresetSelect?.(p);
                        // Presets are an "explicit pick" - close immediately
                        // even if `to` is null (e.g. "All time" preset).
                        setOpen(false);
                      }}
                      className={cn(
                        'rounded-md px-3 py-1.5 text-left text-sm transition-colors',
                        active
                          ? 'bg-blue-50 font-medium text-blue-700'
                          : 'text-zinc-700 hover:bg-zinc-50',
                      )}
                    >
                      {p.label}
                    </button>
                  );
                })}
              </div>
            )}
            <div className="flex flex-col">
              <Calendar
                mode="range"
                numberOfMonths={2}
                selected={
                  selected.from || selected.to
                    ? {
                        from: selected.from ?? undefined,
                        to: selected.to ?? undefined,
                      }
                    : undefined
                }
                onSelect={handleCalendar}
              />
              {/* Footer with explicit Apply/Cancel - auto-close is removed so
                  the operator can pick from then to (or fine-tune either
                  end) without the popover closing under them. */}
              <div className="flex items-center justify-between gap-2 border-t bg-zinc-50/60 px-3 py-2 text-xs">
                <span className="text-zinc-600">
                  {selected.from && selected.to
                    ? `${format(selected.from, dayFmt)} - ${format(selected.to, dayFmt)}`
                    : selected.from
                      ? `${format(selected.from, dayFmt)} - pick end date`
                      : 'Pick a start date'}
                </span>
                <div className="flex gap-1.5">
                  <button
                    type="button"
                    onClick={() => commit({ from: null, to: null })}
                    className="rounded-md px-2 py-1 text-xs text-zinc-600 hover:bg-zinc-100"
                  >
                    Clear
                  </button>
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="rounded-md bg-blue-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-blue-700"
                  >
                    Apply
                  </button>
                </div>
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>
      {error ? (
        <p className="text-destructive text-xs font-medium">{error}</p>
      ) : hint ? (
        <p className="text-muted-foreground text-xs">{hint}</p>
      ) : null}
    </div>
  );
};
